/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useRef, useCallback, useEffect, useState, useImperativeHandle } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { Keys } from '../utils/UNSAFE_keys';
import {
  getPrevNextKeyByCount,
  getViewportConfig,
  findElementByKey,
  keyExtractor,
  handleSelectionRange
} from '../utils/PRIVATE_collectionUtils';

import { useNavigation } from '../hooks/PRIVATE_useListCommon';
import { useSelection, SelectionRangeDetail } from '../hooks/PRIVATE_useSelection';
import { ReorderableContext } from '../hooks/PRIVATE_useReorderable';
import { ItemContext, SelectionDetail } from '../UNSAFE_Collection';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { LoadMoreCollection } from '../PRIVATE_LoadMoreCollection';
import { styles } from './themes/BaseCardViewStyles.css';
import { Props } from '../UNSAFE_CardFlexView';
import { MemoizeBaseCardViewItem } from './BaseCardViewItem';
import { BaseCardViewLayout } from './BaseCardViewLayout';
import {
  DefaultLoadMoreSkeletons,
  LoadMoreSkeletons,
  InitialLoadSkeletons,
  CustomInitialSkeletions,
  CustomLoadMoreSkeletons
} from './BaseCardViewSkeleton';
import { gutterSizeToPX, emptyKeys, CARDVIEW_ITEM_SELECTOR } from './BaseCardViewUtils';
import { useCardViewReorder } from './useCardViewReorder';
// TODO: add this import due to issue JET-67485, need to update once Stable is introduced
import './themes/redwood/BaseCardViewBaseTheme.css';
import { useAddBusyState } from '#hooks/PRIVATE_useAddBusyState';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { classNames } from '#utils/UNSAFE_classNames';

/**
 * Props specific for the GradGridBaseImpl Component
 */
type BaseCardViewImplProps<K, D> = Props<K, D> & {
  /**
   * Provide the number of columns to render;
   */
  columns: number;
  /**
   * The layout of the BaseCardView. When we use CardGridView as parent, we will use grid layout;
   * when we use CardFlexView as parent, we will use flex layout.
   */
  layout: 'grid' | 'flex';
  /**
   * Card dimension, including width and height
   */
  cardSize: { width?: number; height?: number };
  /**
   * A callback function to update card dimension, including width and height
   */
  updateCardSize: (width: number, height: number) => void;
};

const BaseCardViewImpl = forwardRef(
  <K extends string | number, D>(
    {
      children,
      data,
      onLoadMore = () => {},
      hasMore = false,
      getRowKey,
      currentItemOverride,
      onPersistCurrentItem,
      selectionMode = 'none',
      selectedKeys = emptyKeys as Keys<K>,
      onSelectionChange,
      onReorder,
      viewportConfig,
      focusBehavior = 'card',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      initialAnimation = 'slideUp',
      gutterSize = 'sm',
      columns,
      layout,
      cardSize,
      updateCardSize,
      skeletonRenderer
    }: BaseCardViewImplProps<K, D>,
    ref?: ForwardedRef<HTMLDivElement>
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref!, () => rootRef.current!, [rootRef]);

    // tracking the anchor key which is used for shift+click selection
    const anchorKey = useRef<K | undefined>();

    const gutterSizePX = gutterSizeToPX[gutterSize];
    const layoutProps = { layout, columns, gutterSize };

    //setup busy start and busy end callbacks for the initial animation
    const { onBusyStart: onAnimationBusyStart, onBusyEnd: onAnimationBusyEnd } = useAddBusyState(
      'handling initial animation'
    );
    const isInitialAnimation = useRef(true);
    useEffect(() => {
      if (cardSize.height != undefined && cardSize.width != undefined) {
        isInitialAnimation.current = false;
      }
    }, [cardSize]);

    const ariaMultiSelectable = selectionMode === 'none' ? undefined : selectionMode === 'multiple';
    const ariaRowcount = 1;
    const ariaColcount = data ? data.length : -1;

    const [isFocusBehaviorValid, setIsFocusBehaviorValid] = useState(true);
    const validFocusBehavior = isFocusBehaviorValid ? focusBehavior : 'card';

    // ensure current item is scrolled into view when current item is updated
    // during reordering, enable that during keyboard reordering, disable that during mouse reordering
    const scrollCurrItemIntoView = useRef(false);
    const isReorderEnabled = onReorder != undefined;

    const isFetching = useRef(false);

    /*****************************************************************************************/
    /*********************** Current Key, Focus Ring and Tabbable Mode ***********************/
    /*****************************************************************************************/

    const arrowKeyAndHandlers = {
      ArrowUp: (key?: K) => getPrevNextKeyByCount(data, getRowKey, key, -columns),
      ArrowDown: (key?: K) => getPrevNextKeyByCount(data, getRowKey, key, columns),
      ArrowLeft: (key?: K) => getPrevNextKeyByCount(data, getRowKey, key, -1),
      ArrowRight: (key?: K) => getPrevNextKeyByCount(data, getRowKey, key, 1)
    };

    const {
      navigationProps,
      currentKey,
      isCurrentItemOverridden,
      showFocusRing,
      isCurrentTabbableKey
    } = useNavigation(
      data,
      rootRef,
      idExtractor,
      CARDVIEW_ITEM_SELECTOR,
      arrowKeyAndHandlers,
      currentItemOverride,
      onPersistCurrentItem,
      viewportConfig,
      undefined,
      selectionMode !== 'multiple',
      validFocusBehavior === 'card',
      validFocusBehavior === 'card',
      false
    );

    /*****************************************************************************************/
    /*************************************** Selection ***************************************/
    /*****************************************************************************************/

    const handleSelectionChange = useCallback(
      (detail: SelectionDetail<K>) => {
        if (onSelectionChange) {
          if (detail.value.all === false && detail.value.keys.size > 0) {
            // during regular selection, the last key selected by a user gesture is the anchor key
            anchorKey.current = Array.from(detail.value.keys.values()).pop();
          }
          onSelectionChange(detail);
        }
      },
      [anchorKey, onSelectionChange]
    );

    const handleSelectionRangeChange = useCallback(
      (detail: SelectionRangeDetail<K>) => {
        if (data && onSelectionChange) {
          const value = handleSelectionRange(detail, data, getRowKey);
          // for non-virtualized CardView, value should always be an array
          if (Array.isArray(value)) {
            if (detail.eventType === 'shiftSpace') {
              // during range selection by shift+space, the first key selected by a user gesture is the anchor key
              anchorKey.current = detail.value.start;
            } else {
              // during range selection by shift+click/arrow keys, the last key selected by a user gesture is the anchor key
              anchorKey.current = detail.value.end;
            }
            onSelectionChange({ value: { all: false, keys: new Set(value) }, target: null });
          }
        }
      },
      [data, onSelectionChange, getRowKey]
    );

    const { selectionProps } = useSelection(
      (element: HTMLElement) => {
        if (element === rootRef.current) {
          return currentKey === undefined ? null : currentKey;
        }
        return keyExtractor(element, CARDVIEW_ITEM_SELECTOR) as K;
      },
      selectedKeys,
      selectionMode,
      false,
      'toggle',
      handleSelectionChange,
      anchorKey.current,
      currentKey,
      (currentKey: K | undefined, isPrev: boolean) =>
        getPrevNextKeyByCount(data, getRowKey, currentKey, isPrev ? -columns : columns),
      (currentKey: K | undefined, isPrev: boolean) =>
        getPrevNextKeyByCount(data, getRowKey, currentKey, isPrev ? -1 : 1),
      (key: K) => {
        if (rootRef.current) {
          const elem = findElementByKey(rootRef.current, key, CARDVIEW_ITEM_SELECTOR);
          if (elem) {
            elem.scrollIntoView({ block: 'nearest' });
          }
        }
      },
      handleSelectionRangeChange
    );

    /*****************************************************************************************/
    /**************************************** Reorder ****************************************/
    /*****************************************************************************************/

    const {
      reorderProps,
      reorderItemProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } = useCardViewReorder({
      onReorder,
      gutterSize,
      columns,
      rootRef,
      scrollCurrItemIntoView
    });

    const reorderAcc = isReorderEnabled && (
      <>
        <span id={reorderInstructionsId}>
          <HiddenAccessible>{reorderInstructions}</HiddenAccessible>
        </span>
      </>
    );

    const handleKeyDown = (event: KeyboardEvent) => {
      // for content focusBehavior, when users tab out, we need to focus back to card grid, to ensure
      // the tabbing order works properly, otherwise the focus might get stuck at the current item
      if (validFocusBehavior === 'content' && event.key === 'Tab') {
        rootRef.current?.focus({ preventScroll: true });
      }
      // during keyboard reordering, the current item should be scrolled into view
      const keyHandlers = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (
        isReorderEnabled &&
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        keyHandlers.includes(event.key)
      ) {
        scrollCurrItemIntoView.current = true;
      }
    };

    useEffect(() => {
      if (isReorderEnabled && currentKey != null && rootRef.current) {
        const elem = findElementByKey(rootRef.current, currentKey, CARDVIEW_ITEM_SELECTOR);
        // make sure current item is visible during reordering
        // scroll the current item into view only when the current item is updated internally
        if (elem && scrollCurrItemIntoView.current && !isCurrentItemOverridden) {
          elem.scrollIntoView({ block: 'nearest' });
          scrollCurrItemIntoView.current = false;
        }
      }
      // since the currentKey will not change during keyboard reordering,
      // listen to data to ensure cardview scroll into view
    }, [currentKey, data, isReorderEnabled, isCurrentItemOverridden]);

    /*****************************************************************************************/
    /************************************* Load More *****************************************/
    /*****************************************************************************************/

    const handleLoadMore = useCallback(() => {
      // check isFetching to avoid multiple load more at the same time (details in JET-61117)
      if (!isFetching.current) {
        isFetching.current = true;
        onLoadMore();
      }
    }, [isFetching, onLoadMore]);

    useEffect(() => {
      isFetching.current = false;
      // listen to data and hasMore because when one of them gets updated, the fetching is done
    }, [data, hasMore]);

    /*****************************************************************************************/
    /************************************ Child Component ************************************/
    /*****************************************************************************************/

    const childrenComponent = (context: ItemContext<D>) => {
      const itemKey = getRowKey(context.data);
      const isTabbable = isCurrentTabbableKey(itemKey);
      const isFocused = currentKey === itemKey && !isTabbable;
      const isFocusRingVisible = isFocused && showFocusRing;
      const isLastCard = data ? context.index === data.length - 1 : false;
      return (
        <MemoizeBaseCardViewItem<K, D>
          key={itemKey}
          itemKey={itemKey}
          isCurrentKey={currentKey === itemKey}
          itemData={context.data}
          itemIndex={context.index}
          isTabbable={isTabbable}
          isFocused={isFocused}
          isFocusRingVisible={isFocusRingVisible}
          isGridLayout={layout === 'grid'}
          selectedKeys={selectedKeys}
          selectionMode={selectionMode}
          onSelectionChange={onSelectionChange}
          focusBehavior={validFocusBehavior}
          {...(context.index === 0 && isInitialAnimation.current && { onAnimationBusyStart })}
          {...(isLastCard && isInitialAnimation.current && { onAnimationBusyEnd })}
          {...(focusBehavior === 'content' && { setIsFocusBehaviorValid })}
          {...(isInitialAnimation.current && { initialAnimation })}
          {...(context.index === 0 && !cardSize.width && { updateCardSize })}
          {...(isReorderEnabled && { reorderItemProps })}>
          {children}
        </MemoizeBaseCardViewItem>
      );
    };

    // todo: issue track in JET-57952, need a way to show the skeletons in keyboard navigation
    const skeletons = data ? (
      !data.length && hasMore ? (
        skeletonRenderer ? (
          <CustomLoadMoreSkeletons
            layout={layout}
            colCount={columns}
            totalCount={data.length}
            cardWidth={cardSize.width}
            cardHeight={cardSize.height}
            isRenderAsInitial={true}
            skeletonRenderer={skeletonRenderer}
          />
        ) : (
          <DefaultLoadMoreSkeletons layout={layout} />
        )
      ) : skeletonRenderer ? (
        <CustomLoadMoreSkeletons
          layout={layout}
          colCount={columns}
          totalCount={data.length}
          cardWidth={cardSize.width}
          cardHeight={cardSize.height}
          isRenderAsInitial={false}
          skeletonRenderer={skeletonRenderer}
        />
      ) : (
        <LoadMoreSkeletons
          layout={layout}
          colCount={columns}
          totalCount={data.length}
          cardWidth={cardSize.width}
          cardHeight={cardSize.height}
        />
      )
    ) : null;

    viewportConfig = getViewportConfig(rootRef, viewportConfig);
    const collectionComponent = (
      <div role="row">
        <BaseCardViewLayout {...layoutProps}>
          <LoadMoreCollection
            data={data}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loadMoreIndicator={skeletons}
            loadMoreThreshold={gutterSizePX + 4} // gap size + padding
            viewportConfig={viewportConfig}>
            {childrenComponent}
          </LoadMoreCollection>
        </BaseCardViewLayout>
      </div>
    );

    const initialLoadSkeletons = (
      <BaseCardViewLayout {...layoutProps}>
        {skeletonRenderer ? (
          <CustomInitialSkeletions skeletonRenderer={skeletonRenderer} />
        ) : (
          <InitialLoadSkeletons layout={layout} />
        )}
      </BaseCardViewLayout>
    );
    const allowBrowserContextMenu = !(
      getClientHints().platform === 'ios' || getClientHints().platform === 'android'
    );

    const classArray = [styles.baseStyle];
    if (!allowBrowserContextMenu) {
      classArray.push(styles.userSelect);
    }
    const classes = classNames(classArray);

    return (
      <div
        {...mergeProps(navigationProps, selectionProps, reorderProps, {
          onKeyDown: handleKeyDown
        })}
        ref={rootRef}
        role="grid"
        class={classes}
        tabIndex={0}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={
          isReorderEnabled ? reorderInstructionsId + ' ' + ariaDescribedBy : ariaDescribedBy
        }
        aria-multiselectable={ariaMultiSelectable}
        aria-rowcount={ariaRowcount}
        aria-colcount={ariaColcount}>
        <ReorderableContext.Provider value={reorderContext}>
          {!data ? (
            initialLoadSkeletons
          ) : data.length || hasMore ? (
            collectionComponent
          ) : (
            <Fragment />
          )}
        </ReorderableContext.Provider>
        {reorderAcc}
      </div>
    );
  }
);

const idExtractor = (elem: Element) => elem.id;

/**
 * Props specific for the BaseCardView
 */
type BaseCardViewProps<K, D> = BaseCardViewImplProps<K, D> & {
  ref?: ForwardedRef<HTMLDivElement>;
};

/**
 * Cast BaseCardViewImpl to generic type, because BaseCardViewImpl would infer the generic type to specific type,
 * i.e infer <K extends string | number, D> to <string | number, unknown>, which will cause type issues in the parent component
 */
export const BaseCardView = BaseCardViewImpl as <K extends string | number, D>(
  props: BaseCardViewProps<K, D>
) => JSX.Element;
